import { useEffect, useState } from 'react';
import ghlLogo from '@/assets/images/ghl_logo.svg';
import useIntegrationsStore from '@/stores/IntegrationsStore';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveIntegrationSettings } from '@/hooks/integrations';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useGHLPopup } from '@/components/onboarding/GHLPopup';

const GHLSettings = () => {
  const {
    ghl,
    loading: storeLoading,
    setGhlCalendarId,
  } = useIntegrationsStore(
    useShallow((state) => ({
      ghl: state.ghl,
      loading: state.loading,
      setGhlCalendarId: state.setGhlCalendarId,
    }))
  );
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [saving, setSaving] = useState(false);

  const { openGHLPopup, loading: popupLoading } = useGHLPopup();

  // Determine connection status based on ghl data
  const getConnectionStatus = () => {
    if (!ghl) {
      return 'not_connected';
    }

    const isActive = ghl.active === true;
    const hasCalendarId = ghl.current_calendar_id;

    // Pending: isActive is true but hasCalendarId is null/falsy
    if (isActive && !hasCalendarId) {
      return 'pending';
    }

    // Connected: isActive is true and hasCalendarId exists
    if (isActive && hasCalendarId) {
      return 'connected';
    }

    // Not connected: isActive is false
    return 'not_connected';
  };

  const connectionStatus = getConnectionStatus();

  // Set selected calendar from store
  useEffect(() => {
    if (ghl?.current_calendar_id) {
      setSelectedCalendarId(ghl.current_calendar_id);
    }
  }, [ghl]);

  const handleConnect = async () => {
    await openGHLPopup();
  };

  const handleSaveCalendar = async () => {
    if (!selectedCalendarId) {
      toast.error('Please select a calendar');
      return;
    }

    // Find the selected calendar to get details
    const calendars = ghl?.calendars || [];
    const selectedCalendar = calendars.find(
      (cal) => cal.id === selectedCalendarId
    );

    if (!selectedCalendar) {
      toast.error('Selected calendar not found');
      return;
    }

    setSaving(true);
    try {
      await saveIntegrationSettings('ghl', {
        calendar_id: selectedCalendarId,
      });
      toast.success('Calendar saved successfully');
      // Update store with the saved calendar ID
      if (ghl) {
        setGhlCalendarId(selectedCalendarId);
      }
    } catch (error) {
      console.error('Failed to save calendar:', error);
      toast.error(error.response?.data?.message || 'Failed to save calendar');
    } finally {
      setSaving(false);
    }
  };

  if (storeLoading || popupLoading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const calendars = ghl?.calendars || [];
  const selectedCalendar = calendars.find(
    (cal) => cal.id === ghl?.current_calendar_id
  );

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>GHL Integration</h3>
          <p className='text-sm text-muted-foreground'>
            Connect your GHL account to schedule meetings
          </p>
        </div>
      </div>
      <Separator />

      {connectionStatus === 'not_connected' ? (
        <div className='flex flex-col items-center justify-center pt-24'>
          <div className='mb-6 rounded-lg bg-gray-100 p-4'>
            <img src={ghlLogo} alt='GHL' className='h-12 w-12 object-contain' />
          </div>
          <h2 className='mb-2 text-2xl font-bold'>Connect GHL</h2>
          <p className='mb-6 text-center text-muted-foreground'>
            Connect your GHL account to start scheduling meetings
          </p>
          <Button onClick={handleConnect} disabled={popupLoading}>
            {popupLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Connecting...
              </>
            ) : (
              'Connect GHL'
            )}
          </Button>
        </div>
      ) : (
        <div className='space-y-6'>
          {/* Connection Status */}
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='rounded-lg bg-gray-100 p-2'>
                    <img
                      src={ghlLogo}
                      alt='GHL'
                      className='h-6 w-6 object-contain'
                    />
                  </div>
                  <div>
                    <CardTitle>GHL</CardTitle>
                    <CardDescription>
                      {connectionStatus === 'pending'
                        ? 'Setup incomplete'
                        : 'Connected'}
                    </CardDescription>
                  </div>
                </div>
                {connectionStatus === 'pending' ? (
                  <div className='flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1'>
                    <AlertCircle className='h-4 w-4 text-yellow-600' />
                    <span className='text-sm font-medium text-yellow-600'>
                      Pending
                    </span>
                  </div>
                ) : (
                  <div className='flex items-center gap-2 rounded-full bg-green-50 px-3 py-1'>
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                    <span className='text-sm font-medium text-green-600'>
                      Connected
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Calendar Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Calendar</CardTitle>
              <CardDescription>
                Select the calendar to use for scheduling meetings
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {selectedCalendar && (
                <div className='rounded-md bg-muted p-3'>
                  <p className='text-sm font-medium text-foreground'>
                    Current Calendar:{' '}
                    {selectedCalendar.name || selectedCalendarId}
                  </p>
                </div>
              )}
              {calendars.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No calendars found. Please create a calendar in your GHL
                    account.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Select
                    value={selectedCalendarId}
                    onValueChange={setSelectedCalendarId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a calendar' />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map((calendar) => (
                        <SelectItem key={calendar.id} value={calendar.id}>
                          <div className='flex flex-col'>
                            <span className='font-medium'>{calendar.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {connectionStatus === 'pending' && (
                    <Alert>
                      <AlertDescription>
                        Please select a calendar to complete the setup.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Button
                    onClick={handleSaveCalendar}
                    disabled={!selectedCalendarId || saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      'Save Calendar'
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GHLSettings;
